import { Location } from '@angular/common';
import {
  ApplicationRef,
  computed,
  DestroyRef,
  effect,
  ErrorHandler,
  inject,
  Injectable,
  InjectionToken,
  isDevMode,
  signal,
  untracked,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Params, Router } from '@angular/router';

import { filter, map } from 'rxjs';

import {
  isSameUrlModalRequest,
  modalQueryParams,
  readUrlModalRequest,
  toUrlModalParams,
  type UrlModalParams,
  type UrlModalParamValues,
  urlModalRemoval,
  type UrlModalRequest,
} from '@kros-sk/router-stores/module-router-store/query';
import { type KrosModalCloseEvent } from '@kros-sk/ui/modal';

import { SchemaModalRefService } from '../modal/schema-modal-ref.service';
import { OpenedUrlModal } from './opened-url-modal';
import { UrlModalDeclaration, UrlModalsConfig } from './url-modal.types';

export const URL_MODALS_CONFIG = new InjectionToken<UrlModalsConfig>('URL_MODALS_CONFIG');

/**
 * Opens the modal a page declares when the URL names it, and keeps the URL and the modal in step as far as the
 * declaration's `inUrl` asks. Switched on by `provideUrlModals()`; a component declares with `declareUrlModal()`.
 *
 * Every ref it opens is registered in `SchemaModalRefService`, so `close-modal`, completion handlers and
 * `closedFor()` work as they do for a modal opened any other way.
 *
 * Five facts about its neighbours shape the code:
 * - `KrosModalRef` reports a close twice, `beforeClose$` at once and `afterClosed$` 50 ms later. The next modal may open
 *   only after `afterClosed$`, or `KrosModalStateService`'s stack goes out of order. So a closing modal still counts,
 *   and the next one waits for it.
 * - On a Back, `KrosModalStateService` cancels the modal in its `popstate` listener, before the router has moved. So
 *   the modal notes the router's URL when it starts closing. Its close counts as its own if the browser is still at
 *   that URL once the modal is gone and no navigation is under way, or if a navigation the app started moved it
 *   meanwhile; a close that a Back or Forward moved away from is that move's.
 * - A navigation the engine starts cancels the one under way. So while a navigation is under way, the engine opens
 *   nothing from a link and starts no clean-up of its own; it reconciles again once that navigation has settled.
 * - Callers may run `open()` and `declare()` inside an effect, or close a ref there. So wherever their code runs, the
 *   engine reads its own signals `untracked`.
 * - A declaration may prepare its modal (672307). The modal is the engine's from the moment it would have opened:
 *   every rule that closes or replaces it calls the preparation off instead, and the window opens once the preparation
 *   resolves.
 */
@Injectable()
export class UrlModals {
  readonly #router = inject(Router);
  readonly #location = inject(Location);
  readonly #appRef = inject(ApplicationRef);
  readonly #errorHandler = inject(ErrorHandler);
  readonly #modalRefs = inject(SchemaModalRefService);
  readonly #gate = inject(URL_MODALS_CONFIG).gate?.() ?? signal(true);

  /** A navigation is under way, and one the engine started now would cancel it. */
  readonly #isNavigating = computed(() => this.#router.currentNavigation() !== null);

  /** The live declarations of each id, oldest first. The last one is in effect; an earlier one is again once the later ones go. */
  readonly #declarations = signal<ReadonlyMap<string, readonly UrlModalDeclaration[]>>(new Map());

  /** The modal request the URL holds, updated after each navigation. */
  readonly #request = toSignal(
    this.#router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.#requestIn(this.#router.url))
    ),
    { initialValue: this.#requestIn(this.#router.url), equal: isSameUrlModalRequest }
  );

  /** Bumped each time a modal is gone for good, so reconciling runs again and opens whatever waited for it. */
  readonly #modalGone = signal(0);

  /** The engine's modal, open or closing, until its `afterClosed$`. */
  #current: OpenedUrlModal | null = null;

  /** A request the URL may still name for a moment that must not open anything: its modal closed itself, or a click replaced it. */
  #ignoredRequest: UrlModalRequest | null = null;

  /** The URL, path and query, a link param was set to leave: a removal that did not take there is not tried again. */
  #removalStartedAt: string | null = null;

  /** The param a modal that closed while the app navigated may leave behind, taken out once that navigation has landed. */
  #writeAfterNavigation: { readonly request: UrlModalRequest; readonly history: 'push' | 'replace' } | null = null;

  constructor() {
    effect(() => {
      const request = this.#request();
      const declarations = this.#declarations();
      const isGateOpen = this.#gate();
      const isNavigating = this.#isNavigating();
      this.#modalGone();

      if (!isNavigating) {
        untracked(() => this.#reconcile(request, declarations, isGateOpen));
      }
    });

    effect(() => {
      const request = this.#request();

      if (request && isDevMode()) {
        untracked(() => this.#warnOnceStableIfUndeclared(request));
      }
    });

    inject(DestroyRef).onDestroy(() => this.#current?.cancelByEngine());
  }

  /**
   * Opens a declared modal from a click. `always` writes the URL and lets the URL open it; `fromLink` and `never`
   * open it directly and leave the URL alone, and resolve once its window has opened or the modal ended without one.
   * A modal that is closing is waited for first.
   * Whatever request the URL holds when a click replaces a modal is skipped until the URL changes.
   */
  async open(modalId: string, params: UrlModalParamValues = {}): Promise<void> {
    const declaration = untracked(() => inEffect(this.#declarations(), modalId));

    if (!declaration) {
      warnUndeclared(modalId);
      return;
    }

    if (declaration.inUrl === 'always') {
      await this.#writeModalToUrl(modalId, params);
      return;
    }

    let replacesModalInUrl = false;

    while (this.#current) {
      const current = this.#current;

      if (!current.isClosing) {
        if (current.modalId === modalId) {
          current.params.set(toUrlModalParams(params));
          await current.shown;
          return;
        }

        const request = untracked(() => this.#request());

        // Whatever the URL asks for — the modal it holds, or a link param that lingered — must neither cancel the modal
        // opened here nor bring the replaced one back, until the URL changes.
        if (request) {
          this.#ignoredRequest = request;
        }

        if (current.isInUrl) {
          replacesModalInUrl = true;
        }

        current.cancelByEngine();
      }

      await current.closed;
    }

    // Its owner may have gone, or declared it again, while this waited.
    if (untracked(() => inEffect(this.#declarations(), modalId)) !== declaration) {
      return;
    }

    const modal = this.#show(modalId, declaration, toUrlModalParams(params), false);

    // Awaited together, so a failed opening has its handler before the clean-up navigation lets a task pass.
    await Promise.all([replacesModalInUrl ? this.#removeModalFromUrl('replace') : undefined, modal.shown]);
  }

  /**
   * Declares `modalId` until the returned function is called. Prefer `declareUrlModal()`, which ties it to a component.
   * Declarations of one id stack up: the latest is in effect, and an earlier one is again once the later ones go.
   */
  declare(modalId: string, declaration: UrlModalDeclaration): () => void {
    if (isDevMode() && untracked(() => this.#declarations().has(modalId))) {
      console.warn(`The URL modal "${modalId}" is declared twice; the later declaration wins.`);
    }

    this.#declarations.update((declarations) => new Map(declarations).set(modalId, [...(declarations.get(modalId) ?? []), declaration]));

    return () => {
      this.#declarations.update((declarations) => {
        const live = declarations.get(modalId) ?? [];

        if (!live.includes(declaration)) {
          return declarations;
        }

        const others = live.filter((each) => each !== declaration);
        const remaining = new Map(declarations);

        if (others.length > 0) {
          remaining.set(modalId, others);
        } else {
          remaining.delete(modalId);
        }

        return remaining;
      });

      // A modal the URL holds goes with the declaration it opened under, whatever else is declared under its id (D12), and
      // the URL reopens it under the one in effect. So does one still preparing, which has no window to keep (672307). Any
      // other stays open, as it does today when the page behind it is rebuilt.
      if (this.#current?.declaration === declaration && (this.#current.isInUrl || this.#current.isPreparing)) {
        this.#current.cancelByEngine();
      }
    };
  }

  /**
   * Brings the modal on screen in line with the URL. Runs once no navigation is under way, after every navigation,
   * declaration change, gate change and close. The rules, in this order:
   * 1. What the URL has moved away from is forgotten: a request is no longer ignored, and a link param may be taken out
   *    again.
   * 2. A param that a modal closed while the app navigated left in the URL is taken out.
   * 3. While a modal is closing, nothing else happens; its `afterClosed$` runs this again.
   * 4. A modal the URL holds closes once the URL no longer names it.
   * 5. A request opens nothing until a declaration of its id is in effect and the gate is open.
   * 6. A request for the modal already open only updates it — unless a click opened it, its owner has gone, and the
   *    link would make the URL hold it: then it is cancelled and opens anew under the declaration in effect.
   * 7. An ignored request opens nothing.
   * 8. A request for another modal closes the current one first; the new one opens once that one is gone.
   * 9. Otherwise the requested modal opens.
   */
  #reconcile(
    request: UrlModalRequest | null,
    declarations: ReadonlyMap<string, readonly UrlModalDeclaration[]>,
    isGateOpen: boolean
  ): void {
    if (this.#ignoredRequest && !isSameUrlModalRequest(this.#ignoredRequest, request)) {
      this.#ignoredRequest = null;
    }

    if (this.#removalStartedAt && this.#removalStartedAt !== this.#pathAndQuery(this.#router.url)) {
      this.#removalStartedAt = null;
    }

    if (this.#writeAfterNavigation) {
      const { request: closedUnder, history } = this.#writeAfterNavigation;
      this.#writeAfterNavigation = null;

      if (isSameUrlModalRequest(closedUnder, request)) {
        void this.#removeModalFromUrl(history);
      }
    }

    const current = this.#current;

    if (current?.isClosing) {
      return;
    }

    if (current?.isInUrl && current.modalId !== request?.modalId) {
      current.cancelByEngine();
      return;
    }

    const declaration = request ? inEffect(declarations, request.modalId) : undefined;

    if (!request || !declaration || !isGateOpen) {
      return;
    }

    if (current?.modalId === request.modalId) {
      // A fromLink modal a click opened before its owner went: the link would make the URL hold a modal no live
      // declaration answers for, so it reopens fresh under the one in effect (D12).
      if (
        !current.isInUrl &&
        current.declaration.inUrl === 'fromLink' &&
        !declarations.get(current.modalId)?.includes(current.declaration)
      ) {
        current.cancelByEngine();
        return;
      }

      this.#applyLinkToCurrent(current, request);
      return;
    }

    if (isSameUrlModalRequest(this.#ignoredRequest, request)) {
      return;
    }

    if (current) {
      current.cancelByEngine();
      return;
    }

    this.#openFromLink(request, declaration);
  }

  /** A link names the modal already open: its params update, and a `never` modal takes the param out of the URL. */
  #applyLinkToCurrent(current: OpenedUrlModal, request: UrlModalRequest): void {
    current.params.set(request.params);

    if (current.declaration.inUrl === 'never') {
      this.#removeLinkParam();
      return;
    }

    current.isInUrl = true;
  }

  #openFromLink(request: UrlModalRequest, declaration: UrlModalDeclaration): void {
    const isInUrl = declaration.inUrl !== 'never';
    const modal = this.#show(request.modalId, declaration, request.params, isInUrl);

    // A link has no caller to take an opener's error, so it goes where an error thrown in an effect goes.
    modal.shown.catch((failure: unknown) => this.#errorHandler.handleError(failure));

    if (!isInUrl) {
      this.#removeLinkParam();
    }
  }

  /**
   * Takes a `never` modal's link param out of the URL, once per URL: a removal a guard cancelled is not tried again while
   * the URL stays put, and one that a navigation overrode, keeping the param, is tried again at the URL it left.
   */
  #removeLinkParam(): void {
    const url = this.#pathAndQuery(this.#router.url);

    if (this.#removalStartedAt === url) {
      return;
    }

    this.#removalStartedAt = url;
    void this.#removeModalFromUrl('replace');
  }

  #show(modalId: string, declaration: UrlModalDeclaration, params: UrlModalParams, isInUrl: boolean): OpenedUrlModal {
    const paramsSignal = signal(params);
    const modal = new OpenedUrlModal(modalId, declaration, paramsSignal, isInUrl, {
      currentRequest: (): UrlModalRequest | null => untracked(() => this.#request()),
      currentUrl: (): string => this.#pathAndQuery(this.#router.url),
      opened: (ref): void => this.#modalRefs.register(modalId, ref),
      afterClosed: (closed, event): void => this.#afterClosed(closed, event),
    });

    this.#current = modal;
    modal.start(() => untracked(() => declaration.opener(paramsSignal.asReadonly())));

    return modal;
  }

  /**
   * The modal is gone, and the engine lets go of it. A close the modal made itself stands — where it was, or while the
   * app navigated: the engine remembers the request that must not reopen it and cleans the URL, at once or once that
   * navigation has landed. A close that a Back or Forward moved away from is that move's: the engine writes nothing, and
   * an entry that still names the modal reopens it.
   */
  #afterClosed(modal: OpenedUrlModal, event: KrosModalCloseEvent): void {
    if (this.#current === modal) {
      this.#current = null;
    }

    const request = modal.closedByEngine ? null : modal.requestAtClose;

    if (request && this.#closedWhereItWas(modal)) {
      this.#ignoredRequest = request;

      if (modal.isInUrl) {
        this.#removeFromUrlAfterOwnClose(event);
      }
    } else if (request && this.#movedByTheApp()) {
      // A grid filter or a refresh after a submit navigated as the modal closed: the close stands, and a param that
      // navigation kept goes once it has landed.
      this.#ignoredRequest = request;

      if (modal.isInUrl && event.type !== 'redirect') {
        this.#writeAfterNavigation = { request, history: event.type === 'submit' ? 'replace' : 'push' };
      }
    } else if (request && !isSameUrlModalRequest(this.#requestIn(this.#location.path()), request)) {
      // The browser no longer names it — Back to another entry, or a page that rewrote the URL outside the router — so
      // the request the router may still hold must not reopen it.
      this.#ignoredRequest = request;
    }

    this.#modalGone.update((count) => count + 1);
  }

  /** The modal closed itself, no navigation is under way, and the browser is still at the URL the modal closed under. */
  #closedWhereItWas(modal: OpenedUrlModal): boolean {
    return (
      !modal.closedByEngine && !untracked(() => this.#isNavigating()) && this.#pathAndQuery(this.#location.path()) === modal.urlAtClose
    );
  }

  /**
   * The navigation that moved the browser since the modal began to close is one the app started, not a Back or Forward:
   * the one under way, or the one that landed last once the browser agrees with the router again.
   */
  #movedByTheApp(): boolean {
    const browserFollowsRouter = this.#pathAndQuery(this.#location.path()) === this.#pathAndQuery(this.#router.url);
    const navigation = untracked(
      () => this.#router.currentNavigation() ?? (browserFollowsRouter ? this.#router.lastSuccessfulNavigation() : null)
    );

    return navigation?.trigger === 'imperative';
  }

  /** A dismissal pushes a new entry, so Back reopens the modal; done replaces the entry; `redirect` leaves the URL to its navigation. */
  #removeFromUrlAfterOwnClose(event: KrosModalCloseEvent): void {
    if (event.type !== 'redirect') {
      void this.#removeModalFromUrl(event.type === 'submit' ? 'replace' : 'push');
    }
  }

  #writeModalToUrl(modalId: string, params: UrlModalParamValues): Promise<boolean> {
    return this.#router.navigate([], {
      queryParams: { ...this.#clearedModalParams(), ...modalQueryParams(modalId, params) },
      queryParamsHandling: 'merge',
      preserveFragment: true,
    });
  }

  /** The engine's own clean-up. Never while a navigation is under way: it would cancel that one, which owns the URL now. */
  #removeModalFromUrl(history: 'push' | 'replace'): Promise<boolean> {
    if (untracked(() => this.#isNavigating())) {
      return Promise.resolve(false);
    }

    return this.#router.navigate([], {
      queryParams: this.#clearedModalParams(),
      queryParamsHandling: 'merge',
      preserveFragment: true,
      replaceUrl: history === 'replace',
    });
  }

  /** `null` for each `modal` and `modal.*` key in the current URL, which `merge` turns into removals. */
  #clearedModalParams(): Params {
    return urlModalRemoval(this.#router.parseUrl(this.#router.url).queryParams);
  }

  #requestIn(url: string): UrlModalRequest | null {
    return readUrlModalRequest(this.#router.parseUrl(url).queryParams);
  }

  /**
   * A URL's path and query as the router writes them, without the fragment the browser location leaves out, and without
   * a trailing slash: `Location.path()` drops one, the router keeps the empty segment it stands for, and a close under a
   * URL that ends in one would otherwise count as a close the browser moved away from.
   */
  #pathAndQuery(url: string): string {
    const tree = this.#router.parseUrl(url);
    tree.fragment = null;

    return Location.stripTrailingSlash(this.#router.serializeUrl(tree));
  }

  #warnOnceStableIfUndeclared(request: UrlModalRequest): void {
    void this.#appRef.whenStable().then(() => {
      if (isSameUrlModalRequest(this.#request(), request) && !this.#declarations().has(request.modalId)) {
        warnUndeclared(request.modalId);
      }
    });
  }
}

/** The declaration of `modalId` in effect: the latest one still live. */
function inEffect(declarations: ReadonlyMap<string, readonly UrlModalDeclaration[]>, modalId: string): UrlModalDeclaration | undefined {
  return declarations.get(modalId)?.at(-1);
}

function warnUndeclared(modalId: string): void {
  if (isDevMode()) {
    console.warn(
      `No URL modal is declared as "${modalId}" here, so nothing opens. A component declares one with declareUrlModal(), ` +
        'a schema node in its modals — and an entry whose showIf does not hold is not declared.'
    );
  }
}
